/**
 * Get the ordinal suffix for a day number (st, nd, rd, th)
 * @param {number} day - The day of the month (1-31)
 * @returns {string} The ordinal suffix ('st', 'nd', 'rd', or 'th')
 * @example
 * getOrdinalSuffix(1)  // 'st'
 * getOrdinalSuffix(2)  // 'nd'
 * getOrdinalSuffix(3)  // 'rd'
 * getOrdinalSuffix(11) // 'th'
 * getOrdinalSuffix(21) // 'st'
 */
function getOrdinalSuffix(day) {
    // Handle special cases: 11th, 12th, 13th
    if (day > 3 && day < 21) return "th";

    // Handle regular cases based on last digit
    switch (day % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
}

/**
 * Format a date for blog posts in British English format with ordinal suffixes
 * @param {number|Date|string} date - Unix timestamp (ms), Date object, or date string
 * @returns {string} Formatted date string (e.g., "7th December 2025")
 * @example
 * formatBlogDate(1735689600000)  // "1st January 2025"
 * formatBlogDate(new Date())     // "7th December 2024"
 * formatBlogDate('2025-12-07')   // "7th December 2025"
 */
export function formatBlogDate(date) {
    // Handle invalid inputs
    if (!date) {
        return "Invalid date";
    }

    // Convert to Date object
    let dateObj;
    if (date instanceof Date) {
        dateObj = date;
    } else if (typeof date === "number" || typeof date === "string") {
        dateObj = new Date(date);
    } else {
        return "Invalid date";
    }

    // Check if date is valid
    if (Number.isNaN(dateObj.getTime())) {
        return "Invalid date";
    }

    // Get day number
    const day = dateObj.getDate();

    // Get month name (full)
    const month = new Intl.DateTimeFormat("en-GB", { month: "long" }).format(dateObj);

    // Get year
    const year = new Intl.DateTimeFormat("en-GB", { year: "numeric" }).format(dateObj);

    // Combine with ordinal suffix
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}
